import { PrimeMark } from '@/components/PrimeMark'
import { ListFilter } from 'lucide-react'
import { Fragment, useEffect, useRef, useState } from 'react'
import { Link, useRouter, useSearch } from '@tanstack/react-router'
import { Phrases } from '@/components/Phrases'
import { PROJECT_STATE_WORD } from '@/lib/words'
import { api } from '@/runtime'
import type { BrowseResult } from '@/db/types'
import { artistTo, itemTo } from './Insights'
import {
  useAllConcepts,
  useAllLists,
  useAllPages,
  useAllProjects,
  useAllTasks,
  useWorkspaceBySlug,
  useAllEdges,
  useDates,
  usePeople,
} from '@/db/hooks'
import { matchConcept } from '@/lib/learn'
import { Avatar } from '@/components/PersonBits'
import { DateRow } from '@/components/DateBits'
import { nextOccurrence } from '@/lib/dates'
import { todayLocal } from '@/lib/time'
import { withSteps } from '@/lib/steps'
import { PageRow } from '@/kinds/page/components'
import { ListRow } from '@/kinds/list/components'
import { Icon } from '@/lib/icons'
import { useListNav } from '@/hooks/useKeyboard'
import { TaskRow } from '@/components/TaskRow'
import { Empty, PageTitle, SectionTitle } from '@/components/ui'

/** The desktop's keys, said once each (a list, not a run of dots). */
const SHORTCUTS: [string, string][] = [
  ['⌘K', 'search everything'],
  ['⌘\\', 'sidebar'],
  ['j / k', 'move'],
  ['x', 'complete'],
  ['s', 'start or stop'],
  ['e', 'edit'],
  ['d', 'defer'],
  ['p', 'project'],
  ['1–3', 'priority'],
  ['n', 'new'],
  ['g then t, i, u, p, r, w', 'go to a page'],
]

/** Local substring search across kinds (name + notes). No server round-trip. */
/** Remounts when the URL query changes so a sidebar label click lands as a fresh search. */
export const SearchView = () => {
  const params = useSearch({ strict: false }) as { q?: string }
  return <SearchInner key={params.q ?? ''} initial={params.q ?? ''} />
}

const SearchInner = ({ initial }: { initial: string }) => {
  const [q, setQ] = useState(initial)
  const ref = useRef<HTMLInputElement>(null)
  useEffect(() => ref.current?.focus(), [])
  const tasks = useAllTasks() ?? []
  const projects = useAllProjects() ?? []
  const pages = useAllPages() ?? []
  const lists = useAllLists() ?? []
  const concepts = useAllConcepts() ?? []
  const wsBySlug = useWorkspaceBySlug()
  const needle = q.trim().toLowerCase()
  const hit = (name: string, notes?: string) =>
    needle && (name.toLowerCase().includes(needle) || (notes ?? '').toLowerCase().includes(needle))
  const edges = useAllEdges()
  const taskHits = needle ? tasks.filter((t) => hit(t.spec.title, t.spec.notes)).slice(0, 50) : []
  const projectHits = needle
    ? projects.filter(
        (p) => hit(p.spec.title, p.spec.notes) || (p.spec.outcome ?? '').toLowerCase().includes(needle),
      )
    : []
  const pageHits = needle ? pages.filter((n) => !n.deleted_at && hit(n.name, n.spec.text)).slice(0, 30) : []
  const listHits = needle
    ? lists
        .filter((l) => !l.deleted_at && hit(l.spec.title, l.spec.items.map((i) => i.text).join('\n')))
        .slice(0, 20)
    : []
  const conceptHits = needle
    ? concepts.filter((c) => !c.deleted_at && matchConcept(c, needle)).slice(0, 20)
    : []
  // music: artists and tracks from the library, asked of the server after a pause
  const [music, setMusic] = useState<{ needle: string; a: BrowseResult } | null>(null)
  useEffect(() => {
    if (needle.length < 2) return
    let stale = false
    const t = setTimeout(() => {
      api
        .libraryBrowse('all', needle, 6)
        .then((a) => {
          if (!stale) setMusic({ needle, a })
        })
        .catch(() => {})
    }, 300)
    return () => {
      stale = true
      clearTimeout(t)
    }
  }, [needle])
  const hits = music && music.needle === needle ? music.a : null
  const artistHits = hits?.artists ?? []
  const trackHits = hits?.tracks ?? []
  const people = usePeople()
  const dates = useDates()
  const today = todayLocal()
  const personHits = needle
    ? people.filter(
        (p) =>
          hit(p.spec.name, p.spec.notes) ||
          p.spec.key.includes(needle) ||
          (p.spec.aka ?? []).some((a) => a.toLowerCase().includes(needle)),
      )
    : []
  const dateHits = needle
    ? dates
        .filter((d) => hit(d.spec.title, d.spec.notes))
        .map((d) => ({ d, on: nextOccurrence(d.spec, today) || d.spec.date }))
        .slice(0, 20)
    : []
  const cursor = useListNav(taskHits.map((t) => t.id))
  const router = useRouter()
  const leave = () => {
    if (window.history.length > 1) router.history.back()
    else void router.navigate({ to: '/' })
  }
  return (
    <div>
      <PageTitle>Search</PageTitle>
      {/* the field has focus, which hides the tab bar on a phone: Cancel is the way out */}
      <div className="flex items-center gap-2 mb-3">
        <input
          ref={ref}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Tasks, pages, people, dates, music"
          className="w-full"
          type="search"
          aria-label="Search"
          onKeyDown={(e) => {
            if (e.key === 'Escape') setQ('')
            if (e.key === 'ArrowDown') ref.current?.blur()
          }}
        />
        <button
          type="button"
          onClick={leave}
          className="md:hidden shrink-0 min-h-11 px-1 text-[15px] text-accent-text"
          data-testid="search-cancel"
        >
          Cancel
        </button>
      </div>
      {!needle && (
        <p className="text-sm text-muted mt-6 text-center" data-testid="search-hint">
          Try a task, a person, a date or a song.
        </p>
      )}
      {needle &&
        taskHits.length === 0 &&
        projectHits.length === 0 &&
        pageHits.length === 0 &&
        conceptHits.length === 0 &&
        personHits.length === 0 &&
        dateHits.length === 0 && <Empty>No matches.</Empty>}
      {needle.length > 2 && (
        // nothing here, or not the right thing: the same words to Prime, which can search everything and act
        <Link
          to="/agents"
          search={{ ask: q.trim() } as never}
          className="flex items-center gap-3 rounded-[var(--radius-card)] border border-line px-3.5 min-h-12 mb-4 text-[15px] hover:bg-raised/50"
          data-testid="search-ask-prime"
        >
          <PrimeMark size={16} className="text-accent-text shrink-0" aria-hidden />
          <span className="min-w-0 truncate">Ask Prime about “{q.trim()}”</span>
        </Link>
      )}
      {needle && (
        // the same words as a query: All filters them by state, project, day and more
        <Link
          to="/all"
          search={{ q: q.trim() } as never}
          className="flex items-center gap-3 rounded-[var(--radius-card)] border border-line px-3.5 min-h-12 mb-4 text-[15px] hover:bg-raised/50"
          data-testid="search-open-all"
        >
          <ListFilter size={16} className="text-accent-text shrink-0" aria-hidden />
          <span className="min-w-0 truncate">Filter these in All</span>
        </Link>
      )}
      {personHits.length > 0 && (
        <section className="mb-4">
          <h2 className="text-[13px] font-semibold text-muted mb-2">People</h2>
          <ul className="group-list">
            {personHits.map((p) => (
              <li key={p.id}>
                <Link
                  to="/people/$id"
                  params={{ id: p.id }}
                  className="flex items-center gap-3 px-3.5 min-h-12 py-1.5"
                >
                  <Avatar person={p} size={28} />
                  <span className="truncate">{p.spec.name}</span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
      {dateHits.length > 0 && (
        <section className="mb-4">
          <h2 className="text-[13px] font-semibold text-muted mb-2">Dates</h2>
          <ul className="group-list">
            {dateHits.map(({ d, on }) => (
              <DateRow key={d.id} date={d} on={on} today={today} />
            ))}
          </ul>
        </section>
      )}
      {projectHits.length > 0 && (
        <section className="mb-4">
          <h2 className="text-[13px] font-semibold text-muted mb-2">Projects</h2>
          <ul className="group-list">
            {projectHits.map((p) => (
              <li key={p.id}>
                <Link to="/projects/$id" params={{ id: p.id }} className="block px-3 py-2 min-h-12">
                  <span className="mr-1">
                    <Icon value={wsBySlug.get(p.labels.ws ?? '')?.spec.icon} />
                  </span>
                  <Icon value={p.spec.icon} className="mr-1" />
                  {p.spec.title}
                  {p.status.state !== 'active' && (
                    <span className="text-xs text-faint ml-2">{PROJECT_STATE_WORD[p.status.state]}</span>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
      {taskHits.length > 0 && (
        <section>
          <h2 className="text-[13px] font-semibold text-muted mb-2">Tasks</h2>
          <ul className="group-list">
            {withSteps(taskHits, edges).map(({ task: t, child }, i) => (
              <TaskRow key={t.id} task={t} index={i} focused={cursor === i} mode="compact" child={child} />
            ))}
          </ul>
        </section>
      )}
      {pageHits.length > 0 && (
        <section className="mt-4">
          <h2 className="text-[13px] font-semibold text-muted mb-2">Pages</h2>
          <ul className="group-list" data-testid="search-pages">
            {pageHits.map((n) => (
              <PageRow
                key={n.id}
                resource={n}
                hint={<Icon value={wsBySlug.get(n.labels.ws ?? '')?.spec.icon} size={14} />}
              />
            ))}
          </ul>
        </section>
      )}
      {conceptHits.length > 0 && (
        <section className="mt-4" data-testid="search-concepts">
          <h2 className="text-[13px] font-semibold text-muted mb-2">Concepts</h2>
          <ul className="group-list">
            {conceptHits.map((c) => (
              <li key={c.id}>
                <Link to="/learn/concept/$id" params={{ id: c.id }} className="block px-3.5 py-2 min-h-12">
                  <span className="block text-[15px] truncate">{c.spec.name}</span>
                  {c.spec.one_liner && (
                    <span className="block text-xs text-muted truncate">{c.spec.one_liner}</span>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
      {(artistHits.length > 0 || trackHits.length > 0) && (
        <section className="mb-4" data-testid="search-music">
          <SectionTitle
            right={
              <Link to="/music" className="text-xs underline text-muted">
                Music
              </Link>
            }
          >
            Music
          </SectionTitle>
          <ul className="group-list">
            {artistHits.map((a) => (
              <li key={`a-${a.key}`}>
                <Link {...artistTo(a.key)} className="flex items-center gap-3 px-3 py-2 hover:bg-raised/60">
                  {a.image ? (
                    <img src={a.image} alt="" className="w-8 h-8 rounded-full object-cover shrink-0" />
                  ) : (
                    <span className="w-8 h-8 rounded-full bg-raised shrink-0" />
                  )}
                  <span className="min-w-0 flex-1 text-sm truncate">{a.key}</span>
                  <Phrases
                    className="text-xs text-faint shrink-0"
                    parts={['Artist', `${a.count.toLocaleString()} plays`]}
                  />
                </Link>
              </li>
            ))}
            {trackHits.map((t) => (
              <li key={`t-${t.id}`}>
                <Link {...itemTo(t.id)} className="flex items-center gap-3 px-3 py-2 hover:bg-raised/60">
                  {t.data.image ? (
                    <img src={t.data.image} alt="" className="w-8 h-8 rounded-[6px] object-cover shrink-0" />
                  ) : (
                    <span className="w-8 h-8 rounded-[6px] bg-raised shrink-0" />
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm truncate">{t.title}</span>
                    <span className="block text-xs text-faint truncate">{String(t.data.artist ?? '')}</span>
                  </span>
                  <span className="text-xs text-faint shrink-0">{t.count.toLocaleString()} plays</span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
      {listHits.length > 0 && (
        <section className="mt-4">
          <h2 className="text-[13px] font-semibold text-muted mb-2">Lists</h2>
          <ul className="group-list" data-testid="search-lists">
            {listHits.map((l) => (
              <ListRow key={l.id} resource={l} />
            ))}
          </ul>
        </section>
      )}
      <section className="mt-8 hidden md:block" aria-label="Shortcuts">
        <h2 className="text-[13px] font-semibold text-muted mb-2">Shortcuts</h2>
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-xs text-faint max-w-sm">
          {SHORTCUTS.map(([keys, what]) => (
            <Fragment key={keys}>
              <dt>
                <kbd className="font-sans rounded border border-line bg-raised px-1.5 py-px text-fg-2">
                  {keys}
                </kbd>
              </dt>
              <dd>{what}</dd>
            </Fragment>
          ))}
        </dl>
      </section>
    </div>
  )
}
